import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import * as PropTypes from 'prop-types';
import { Text, mergeStyleSets, Stack, Icon } from '@fluentui/react';
import classNames from 'classnames';
import { isNil } from 'lodash-es';
import { useSelector } from 'react-redux';
import { NeutralColors, SharedColors } from '@fluentui/theme';
import { storeFeedback } from 'services/feedback';
import { Constants } from '@shared/utils/constants';
import { logTelemetry } from '@shared/utils/telemetryUtils';
const contentStyles = mergeStyleSets({
    containerClass: {
        paddingTop: 8
    },
    feedbackButtons: {
        color: NeutralColors.gray160,
        '&:hover': {
            color: SharedColors.cyanBlue10
        }
    },
    feedbackButtonsSelected: {
        color: SharedColors.cyanBlue10,
        cursor: 'default'
    },
    defaultCursor: {
        cursor: 'default'
    },
    pointerCursor: {
        cursor: 'pointer'
    }
});
const Negative = 'Negative';
const Positive = 'Positive';
const getAriaLabel = ({ isFeedbackNull, isFeedbackPositive, feedbackNoun, context })=>{
    const isPositiveFeedbackSelected = !isFeedbackNull && isFeedbackPositive;
    const isNegativeFeedbackSelected = !isFeedbackNull && !isFeedbackPositive;
    const selectionCondition = feedbackNoun === Negative ? isPositiveFeedbackSelected : isNegativeFeedbackSelected;
    const feedbackText = feedbackNoun === Positive ? Positive : Negative;
    return isFeedbackNull || selectionCondition ? context.loc(`Feedback_ShowingResultsFor_${feedbackText}Feedback`, `${feedbackText} feedback`) : context.loc(`Feedback_ShowingResultsFor_${feedbackText}FeedbackProvided`, `Provided ${feedbackText} feedback`);
};
export const sendFeedback = ({ isPositive, requestId, showingResultsFor, didYouMean, previousSearchText })=>{
    const details = {
        positiveFeedBack: isPositive,
        requestId,
        showingResultsFor,
        didYouMean,
        searchTerm: previousSearchText
    };
    logTelemetry(Constants.Telemetry.Action.Feedback, Constants.Telemetry.ActionModifier.ShowingResultsForFeedback, JSON.stringify(details), true);
    storeFeedback({
        action: Constants.Telemetry.Action.Click,
        actionModifier: Constants.Telemetry.ActionModifier.ShowingResultsForFeedback,
        details: JSON.stringify(details)
    });
};
const likeIconName = 'Like';
const solidLikeIconName = 'LikeSolid';
const dislikeIconName = 'Dislike';
const solidDislikeIconName = 'DislikeSolid';
export const ThumbsUpDown = ({ localizedQuestion = '' }, context)=>{
    const { containerClass, feedbackButtons, feedbackButtonsSelected, defaultCursor, pointerCursor } = contentStyles;
    const [isFeedbackPositive, setFeedback] = useState(null);
    const { requestId, showingResultsFor, didYouMean, previousSearchText } = useSelector(({ search: { requestId, showingResultsFor, didYouMean, previousSearchText } })=>({
            requestId,
            showingResultsFor,
            didYouMean,
            previousSearchText
        }));
    const isFeedbackNull = isNil(isFeedbackPositive);
    const likeAria = getAriaLabel({
        isFeedbackNull,
        feedbackNoun: Positive,
        isFeedbackPositive,
        context
    });
    const dislikeAria = getAriaLabel({
        isFeedbackNull,
        feedbackNoun: Negative,
        isFeedbackPositive,
        context
    });
    const handleFeedbackClick = ({ isPositive })=>{
        if (isFeedbackNull) {
            setFeedback(isPositive);
            sendFeedback({
                isPositive,
                requestId,
                showingResultsFor,
                didYouMean,
                previousSearchText
            });
        }
    };
    const detectKeyboardClick = ({ event: { key }, isPositive })=>{
        if (key === Constants.SystemKeyAsString.Enter || key === Constants.SystemKeyAsString.Space) {
            handleFeedbackClick({
                isPositive
            });
        }
    };
    const positiveIconName = isFeedbackNull ? likeIconName : isFeedbackPositive ? solidLikeIconName : likeIconName;
    const negativeIconName = isFeedbackNull ? dislikeIconName : !isFeedbackPositive ? solidDislikeIconName : dislikeIconName;
    const thumbsUpClass = classNames({
        [defaultCursor]: !isFeedbackNull && !isFeedbackPositive,
        [feedbackButtons]: isFeedbackNull,
        [pointerCursor]: isFeedbackNull,
        [feedbackButtonsSelected]: !isFeedbackNull && isFeedbackPositive
    });
    const thumbsDownClass = classNames({
        [defaultCursor]: !isFeedbackNull && isFeedbackPositive,
        [feedbackButtons]: isFeedbackNull,
        [pointerCursor]: isFeedbackNull,
        [feedbackButtonsSelected]: !isFeedbackNull && !isFeedbackPositive
    });
    return /*#__PURE__*/ _jsx("div", {
        children: /*#__PURE__*/ _jsxs(Stack, {
            horizontal: true,
            className: containerClass,
            tokens: {
                childrenGap: 6
            },
            children: [
                /*#__PURE__*/ _jsx(Stack.Item, {
                    children: /*#__PURE__*/ _jsx(Text, {
                        children: localizedQuestion
                    })
                }),
                /*#__PURE__*/ _jsx(Stack.Item, {
                    children: /*#__PURE__*/ _jsx(Icon, {
                        role: 'button',
                        "aria-label": likeAria,
                        tabIndex: 0,
                        onClick: ()=>handleFeedbackClick({
                                isPositive: true
                            }),
                        onKeyDown: (event)=>detectKeyboardClick({
                                event,
                                isPositive: true
                            }),
                        iconName: positiveIconName,
                        className: thumbsUpClass
                    })
                }),
                /*#__PURE__*/ _jsx(Stack.Item, {
                    children: /*#__PURE__*/ _jsx(Icon, {
                        role: 'button',
                        "aria-label": dislikeAria,
                        tabIndex: 0,
                        onClick: ()=>handleFeedbackClick({
                                isPositive: false
                            }),
                        onKeyDown: (event)=>detectKeyboardClick({
                                event,
                                isPositive: false
                            }),
                        iconName: negativeIconName,
                        className: thumbsDownClass
                    })
                })
            ]
        })
    });
};
ThumbsUpDown.contextTypes = {
    loc: PropTypes.func
};
